import React, { useEffect, useState } from "react";
import TemplateMessage from "./TemplateMessage";
import MediaMessage from "./MediaMessage";
import PayloadMessage from "./PayloadMessage";
import StatusMessage from "./StatusMessage";
import { useAuth } from "../services/auth.service";
import SurveyMessage from "./SurveyMessage";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Tooltip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Grid
} from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';

const fadeInSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// Helper translation functions
const translateMessageType = (type) => {
  const typesMap = {
    text: 'Texto',
    image: 'Imagen',
    video: 'Video',
    audio: 'Audio',
    document: 'Documento / Archivo',
    file: 'Archivo',
    sticker: 'Sticker',
    location: 'Ubicación',
    contact: 'Contacto',
    contacts: 'Contacto',
    interactive: 'Botón Interactivo',
    button: 'Botón Interactivo',
    template: 'Plantilla',
    reaction: 'Reacción',
  };
  return typesMap[type] || type || 'Texto';
};

const translateCategory = (cat) => {
  const catMap = {
    service: 'Servicio al Cliente',
    utility: 'Utilidad',
    marketing: 'Marketing',
    authentication: 'Autenticación',
  };
  return catMap[cat] || cat || 'N/A';
};

const translatePricingType = (type) => {
  const pricingMap = {
    free_customer_service: 'Servicio al Cliente Gratuito',
    regular: 'Regular / Estándar',
  };
  return pricingMap[type] || type || 'N/A';
};

// Styled components for message bubbles matching mockup
const MessageBubble = styled(Box)(({ theme, isSender, isStatus }) => ({
  position: 'relative',
  padding: isStatus ? '8px 16px' : '12px 20px',
  borderRadius: '20px',
  maxWidth: '65%',
  minWidth: '100px',
  backgroundColor: isSender
    ? '#f0f7ff'
    : isStatus
      ? '#f8f9fa'
      : '#f8f9fa',
  color: '#1e293b',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02)',
  border: isSender ? '1px solid #e0f2fe' : '1px solid #f0f2f5',
  wordWrap: 'break-word',
  transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
  '&:hover': {
    boxShadow: '0 3px 10px rgba(0, 0, 0, 0.04)',
  }
}));

const MessageContainer = styled(Box)(({ theme, isSender, isStatus }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  marginBottom: '18px',
  animation: `${fadeInSlideUp} 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards`,
}));

const BubbleRow = styled(Box)(({ theme, isSender, isStatus }) => ({
  display: 'flex',
  justifyContent: isStatus ? 'center' : isSender ? 'flex-end' : 'flex-start',
  alignItems: 'center',
  gap: '10px',
  padding: '0 16px',
}));

const MessageHeaderRow = styled(Box)(({ theme, isSender }) => ({
  display: 'flex',
  justifyContent: isSender ? 'flex-end' : 'flex-start',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '4px',
  paddingLeft: isSender ? '0' : '58px',
  paddingRight: isSender ? '58px' : '0',
}));

const SenderName = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 700,
  color: '#1e293b',
}));

const TimeStamp = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  color: '#94a3b8',
  fontWeight: 500,
}));

const AvatarStyled = styled(Avatar)(({ theme, isSender }) => ({
  width: '32px',
  height: '32px',
  backgroundColor: isSender ? '#eff6ff' : '#f1f5f9',
  color: isSender ? '#2563eb' : '#64748b',
  fontSize: '13px',
  fontWeight: 600,
  border: isSender ? '1px solid #dbeafe' : '1px solid #e2e8f0',
}));

const ReactionChip = styled(Chip)(({ theme, isSender }) => ({
  fontSize: '12px',
  backgroundColor: isSender ? 'rgba(255, 255, 255, 0.1)' : '#f0f2f5',
  color: isSender ? '#ffffff' : '#667781',
  border: isSender ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid #e9edef',
  marginTop: '4px',
  '& .MuiChip-label': {
    fontSize: '11px',
    padding: '2px 6px',
  },
}));

const ButtonResponseChip = styled(Box)(({ theme, isSender }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4px',
  backgroundColor: isSender ? 'rgba(255, 255, 255, 0.15)' : '#e8f5e8',
  color: isSender ? '#ffffff' : '#008069',
  border: isSender ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid #d4edda',
  borderRadius: '12px',
  padding: '4px 8px',
  marginTop: '4px',
  fontSize: '11px',
  fontWeight: 500,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: isSender ? 'rgba(255, 255, 255, 0.2)' : '#d1e7dd',
  },
}));

function Message(props) {
  const [message, setMessage] = useState(null);
  const [statuses, setStatuses] = useState(null);
  const [error, setError] = useState(false);
  const { user } = useAuth();
  const surveyId = "65cfa291d2cf9544d0ee5667";
  const [survey, setSurvey] = React.useState();
  const [isReaction, setIsReaction] = useState(false);
  const [parentMessage, setParentMessage] = useState(null);
  const [buttonResponses, setButtonResponses] = useState([]);
  const [contextMenu, setContextMenu] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const getNormalizedTextBody = (msg) => {
    const rawBody = msg?.text?.body;
    if (!rawBody) return rawBody;
    if (typeof rawBody === "object" && rawBody.body && typeof rawBody.body === "object") {
      return rawBody.body;
    }
    return rawBody;
  };

  const findLatestStatus = (msgId, messageProp, messagesArray) => {
    let latestStatus = messageProp?.entry?.[0]?.changes?.[0]?.value?.statuses?.slice(-1)[0] || null;
    if (!msgId || !Array.isArray(messagesArray)) return latestStatus;

    const statusPriority = { failed: 4, read: 3, delivered: 2, sent: 1 };

    messagesArray.forEach((item) => {
      try {
        const statusesList = item?.entry?.[0]?.changes?.[0]?.value?.statuses;
        if (Array.isArray(statusesList)) {
          statusesList.forEach((st) => {
            if (st?.id === msgId) {
              if (!latestStatus) {
                latestStatus = st;
              } else {
                const timeSt = Number(st.timestamp || 0);
                const timeLatest = Number(latestStatus.timestamp || 0);
                if (timeSt > timeLatest) {
                  latestStatus = st;
                } else if (timeSt === timeLatest) {
                  const prioSt = statusPriority[st.status] || 0;
                  const prioLatest = statusPriority[latestStatus.status] || 0;
                  if (prioSt >= prioLatest) {
                    latestStatus = st;
                  }
                }
              }
            }
          });
        }
      } catch (e) {
        // ignore
      }
    });

    return latestStatus;
  };

  const handleContextMenu = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu(
      contextMenu === null
        ? { mouseX: event.clientX - 2, mouseY: event.clientY - 4 }
        : null,
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const handleOpenDetails = () => {
    handleCloseContextMenu();
    setShowDetailsDialog(true);
  };

  useEffect(() => {
    const newMessage =
      props.message?.entry?.[0]?.changes?.[0]?.value?.messages?.[0] || null;
    setMessage(newMessage);

    const resolvedStatus = findLatestStatus(newMessage?.id, props.message, props.messages);
    setStatuses(resolvedStatus);

    // Debug logging for media messages
    if (newMessage && (newMessage.type === "file" || newMessage.type === "image" || newMessage.type === "video" || newMessage.type === "audio" || newMessage.type === "document")) {
      console.log('Media message detected:', newMessage);
    }

    // Check if this is a reaction message
    if (newMessage?.type === "reaction") {
      setIsReaction(true);
      // Find the parent message that this reaction belongs to
      const reactedMessageId = newMessage.reaction.message_id;
      // You'll need to pass the messages array as a prop to find the parent
      if (props.messages && props.messages.length > 0) {
        const parent = props.messages.find(msg => {
          try {
            return msg?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.id === reactedMessageId;
          } catch (error) {
            return false;
          }
        });
        setParentMessage(parent);
      }
    } else {
      setIsReaction(false);
      setParentMessage(null);
    }

    // Find button responses for this message
    if (newMessage && props.messages && props.messages.length > 0) {
      const responses = props.messages.filter(msg => {
        try {
          const responseMsg = msg?.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
          return responseMsg?.type === "button" &&
            responseMsg?.context?.id === newMessage.id;
        } catch (error) {
          return false;
        }
      });
      setButtonResponses(responses);
    } else {
      setButtonResponses([]);
    }
  }, [props.message, props.messages]);

  useEffect(() => {
    const newMessage = props.message?.entry?.[0]?.changes?.[0]?.value?.messages?.[0] || null;
    const resolvedStatus = findLatestStatus(newMessage?.id, props.message, props.messages);
    setStatuses(resolvedStatus);
  }, [JSON.stringify(props.message), JSON.stringify(props.messages)]);

  useEffect(() => {
    if (message?.type === "text") {
      if (message?.text?.body?.name) {
        console.log("Template message");
      } else {
        if (message?.text.body.text) {
          console.log("Text message with subtext")
        }
        console.log("Text message");
      }
    } else {
      console.log("Media message");
    }
  }, [message]);

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderButtonResponses = (isSender) => {
    if (!buttonResponses || buttonResponses.length === 0) return null;

    return buttonResponses.map((responseMsg, idx) => {
      try {
        const buttonMsg = responseMsg?.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
        const buttonText = buttonMsg?.button?.text || 'Button Response';
        const buttonPayload = buttonMsg?.button?.payload || '';
        const timestamp = formatTime(buttonMsg?.timestamp);

        const tooltipContent = (
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              Button Response Details:
            </Typography>
            <Typography variant="caption" display="block">
              Text: {buttonText}
            </Typography>
            <Typography variant="caption" display="block">
              Payload: {buttonPayload}
            </Typography>
            <Typography variant="caption" display="block">
              Time: {timestamp}
            </Typography>
            <Typography variant="caption" display="block">
              ID: {buttonMsg?.id}
            </Typography>
          </Box>
        );

        return (
          <Tooltip key={idx} title={tooltipContent} arrow placement="top">
            <ButtonResponseChip isSender={isSender}>
              <span style={{ fontSize: '10px' }}>🔘</span>
              {buttonText}
            </ButtonResponseChip>
          </Tooltip>
        );
      } catch (error) {
        console.error('Error rendering button response:', error);
        return null;
      }
    });
  };

  const renderMessage = () => {
    // If this is a reaction message, don't render it as a separate message
    if (message?.type === "reaction") {
      return null; // Reactions will be handled by their parent messages
    }

    // If this is a button response message, don't render it as a separate message
    if (message?.type === "button" && message?.context?.id) {
      return null; // Button responses will be handled by their parent messages
    }

    // If this is a standalone status update entry (no messages array), don't render as separate bubble
    if (!message && props.message?.entry?.[0]?.changes?.[0]?.value?.statuses) {
      return null;
    }

    if (!message || !message.from || !message.timestamp) {
      return (
        <MessageContainer isSender={true}>
          <MessageHeaderRow isSender={true}>
            <SenderName>{process.env.REACT_APP_PHONE_NAME}</SenderName>
            <TimeStamp>{message ? formatTime(message.timestamp) : ""}</TimeStamp>
          </MessageHeaderRow>
          <BubbleRow isSender={true}>
            <MessageBubble isSender={true} onContextMenu={handleContextMenu}>
              <Typography variant="body2" sx={{ color: 'inherit' }}>
                This message is not supported
              </Typography>
            </MessageBubble>
            <AvatarStyled isSender={true} src="/icon.png">
              M
            </AvatarStyled>
          </BubbleRow>
        </MessageContainer>
      );
    } else if (message?.from === process.env.REACT_APP_PHONE_NUMBER) {
      const normalizedBody = getNormalizedTextBody(message);
      return (
        <MessageContainer isSender={true} isStatus={message.custom_status}>
          {!message.custom_status && (
            <MessageHeaderRow isSender={true}>
              <SenderName>{process.env.REACT_APP_PHONE_NAME}</SenderName>
              <TimeStamp>{formatTime(message.timestamp)}</TimeStamp>
              <StatusMessage statuses={statuses} />
            </MessageHeaderRow>
          )}
          <BubbleRow isSender={true} isStatus={message.custom_status}>
            <MessageBubble isSender={true} isStatus={message.custom_status} onContextMenu={handleContextMenu}>
              {normalizedBody?.name ? (
                <TemplateMessage message={message} sendTemplateMessage={props.sendTemplateMessage} cancelTemplateMessage={props.cancelTemplateMessage} />
              ) : message?.text?.body ? (
                typeof normalizedBody === "string" ? (
                  <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.5, fontSize: '14px' }}>
                    {normalizedBody}
                  </Typography>
                ) : (
                  normalizedBody?.type === "text" ? (
                    <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.5, fontSize: '14px' }}>
                      {normalizedBody?.text?.body || normalizedBody?.body || ""}
                    </Typography>
                  ) : (
                    <MediaMessage type={normalizedBody?.type || message?.type} media={normalizedBody || message} isSender={true} />
                  )
                )
              ) : message?.type && (message.type === "file" || message.type === "image" || message.type === "video" || message.type === "audio" || message.type === "document") ? (
                <MediaMessage type={message.type} media={message} isSender={true} />
              ) : (
                <Typography variant="body2" sx={{ color: 'inherit' }}>
                  This message is not supported
                </Typography>
              )}
              {statuses?.status === 'failed' && (
                <Box sx={{ mt: 0.5, p: 0.5, px: 1, backgroundColor: 'rgba(239, 68, 68, 0.2)', borderRadius: '4px', border: '1px solid rgba(239, 68, 68, 0.4)', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span style={{ fontSize: '12px' }}>⚠️</span>
                  <Typography variant="caption" sx={{ color: '#ffebee', fontWeight: 600, fontSize: '10.5px' }}>
                    No entregado: {(() => {
                      const t = statuses?.errors?.[0]?.error_data?.details || statuses?.errors?.[0]?.title || "";
                      return (!t || t.toLowerCase().includes("undeliverable")) ? "Mensaje no entregable" : t;
                    })()}
                  </Typography>
                </Box>
              )}
              {/* Display reactions for this message */}
              {props.messages && props.messages
                .filter(msg => {
                  try {
                    const m = msg?.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
                    return m?.type === "reaction" && m?.reaction?.message_id === message.id;
                  } catch (error) {
                    return false;
                  }
                })
                .map((reactionMsg, idx) => (
                  <ReactionChip
                    key={idx}
                    label={reactionMsg?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.reaction?.emoji}
                    size="small"
                    isSender={true}
                  />
                ))}
              {/* Display button responses for this message */}
              {renderButtonResponses(true)}
            </MessageBubble>
            {!message.custom_status && (
              <AvatarStyled isSender={true} src="/icon.png">
                M
              </AvatarStyled>
            )}
          </BubbleRow>
        </MessageContainer>
      );
    } else if (message?.interactive?.nfm_reply) {
      return (
        <MessageContainer isSender={true}>
          <MessageHeaderRow isSender={true}>
            <SenderName>{process.env.REACT_APP_PHONE_NAME}</SenderName>
            <TimeStamp>{formatTime(message.timestamp)}</TimeStamp>
          </MessageHeaderRow>
          <BubbleRow isSender={true}>
            <MessageBubble isSender={true} onContextMenu={handleContextMenu}>
              <SurveyMessage responseJson={message.interactive.nfm_reply.response_json} />
            </MessageBubble>
            <AvatarStyled isSender={true} src="/icon.png">
              M
            </AvatarStyled>
          </BubbleRow>
        </MessageContainer>
      );
    } else {
      const normalizedBody = getNormalizedTextBody(message);
      const clientName = message?.profile?.name || message?.from || "Dianne Russell";
      return (
        <MessageContainer isSender={false}>
          <MessageHeaderRow isSender={false}>
            <SenderName>{clientName}</SenderName>
            <TimeStamp>{formatTime(message.timestamp)}</TimeStamp>
          </MessageHeaderRow>
          <BubbleRow isSender={false}>
            <AvatarStyled isSender={false} src="/profile1.jpg">
              C
            </AvatarStyled>
            <MessageBubble isSender={false} onContextMenu={handleContextMenu}>
              {message?.type === "text" ? (
                <Typography variant="body2" sx={{ color: 'inherit', lineHeight: 1.5, fontSize: '14px' }}>
                  {typeof normalizedBody === "string"
                    ? normalizedBody
                    : (normalizedBody?.text?.body || normalizedBody?.body || normalizedBody?.name || "[Mensaje]")}
                </Typography>
              ) : (
                <MediaMessage type={message?.type} media={message} isSender={false} />
              )}
              {message?.type === "button" && (
                <PayloadMessage message={message} />
              )}
              {/* Display reactions for this message */}
              {props.messages && props.messages
                .filter(msg => {
                  try {
                    const m = msg?.entry?.[0]?.changes?.[0]?.value?.messages?.[0];
                    return m?.type === "reaction" && m?.reaction?.message_id === message.id;
                  } catch (error) {
                    return false;
                  }
                })
                .map((reactionMsg, idx) => (
                  <ReactionChip
                    key={idx}
                    label={reactionMsg?.entry?.[0]?.changes?.[0]?.value?.messages?.[0]?.reaction?.emoji}
                    size="small"
                    isSender={false}
                  />
                ))}
              {/* Display button responses for this message */}
              {renderButtonResponses(false)}
            </MessageBubble>
          </BubbleRow>
        </MessageContainer>
      );
    }
  };

  const renderContextMenuAndDialog = () => (
    <>
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        <MenuItem onClick={handleOpenDetails} sx={{ fontSize: '14px', gap: '8px' }}>
          <span style={{ fontSize: '16px' }}>ℹ️</span> Detalles del mensaje
        </MenuItem>
      </Menu>

      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 'bold', fontSize: '18px', borderBottom: '1px solid #e0e0e0', pb: 1.5 }}>
          📋 Detalles del Mensaje
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* General Info */}
            <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: '8px' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#008069', mb: 1 }}>
                Información General
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>ID Mensaje:</Typography></Grid>
                <Grid item xs={8}><Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{message?.id || statuses?.id || 'N/A'}</Typography></Grid>

                <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Tipo:</Typography></Grid>
                <Grid item xs={8}><Typography variant="body2">{translateMessageType(message?.type)}</Typography></Grid>

                <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Estado Actual:</Typography></Grid>
                <Grid item xs={8}>
                  <Chip
                    label={statuses?.status === 'failed' ? 'NO ENTREGABLE / FALLIDO' : statuses?.status === 'read' ? 'LEÍDO' : statuses?.status === 'delivered' ? 'ENTREGADO' : statuses?.status === 'sent' ? 'ENVIADO' : 'PENDIENTE'}
                    size="small"
                    color={statuses?.status === 'failed' ? 'error' : statuses?.status === 'read' ? 'success' : statuses?.status === 'delivered' ? 'info' : 'default'}
                    sx={{ height: '22px', fontSize: '11px', fontWeight: 'bold' }}
                  />
                </Grid>

                <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Fecha / Hora:</Typography></Grid>
                <Grid item xs={8}>
                  <Typography variant="body2">
                    {message?.timestamp ? new Date(message.timestamp * 1000).toLocaleString() : (statuses?.timestamp ? new Date(statuses.timestamp * 1000).toLocaleString() : 'N/A')}
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            {/* Error / Failure Details */}
            {(statuses?.status === 'failed' || (statuses?.errors && statuses.errors.length > 0)) && (
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#fef2f2', borderColor: '#fca5a5', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#dc2626', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <span>⚠️</span> Error en la Entrega (No entregable)
                </Typography>
                {(() => {
                  const errObj = statuses?.errors?.[0] || {};
                  const rawTitle = errObj.title || "";
                  const titleEs = (!rawTitle || rawTitle.toLowerCase().includes("undeliverable")) ? "Mensaje no entregable" : rawTitle;
                  return (
                    <Grid container spacing={1}>
                      <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Código Error:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2" sx={{ fontWeight: 'bold', color: '#b91c1c' }}>{errObj.code || '131026'}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Título:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{titleEs}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Detalles:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2" sx={{ color: '#991b1b', fontWeight: 500 }}>{errObj.error_data?.details || errObj.message || 'El mensaje no se pudo entregar al destinatario.'}</Typography></Grid>
                    </Grid>
                  );
                })()}
              </Paper>
            )}

            {/* Conversation Details */}
            {(statuses?.conversation || message?.conversation) && (
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#1976d2', mb: 1 }}>
                  💬 Detalles de la Conversación
                </Typography>
                {(() => {
                  const conv = statuses?.conversation || message?.conversation;
                  return (
                    <Grid container spacing={1}>
                      <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>ID Conversación:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{conv?.id || 'N/A'}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Origen / Tipo:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{translateCategory(conv?.origin?.type)}</Typography></Grid>

                      {conv?.expiration_timestamp && (
                        <>
                          <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Expiración:</Typography></Grid>
                          <Grid item xs={8}><Typography variant="body2">{new Date(conv.expiration_timestamp * 1000).toLocaleString()}</Typography></Grid>
                        </>
                      )}
                    </Grid>
                  );
                })()}
              </Paper>
            )}

            {/* Pricing Details */}
            {(statuses?.pricing || message?.pricing) && (
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#f9fafb', borderRadius: '8px' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#ed6c02', mb: 1 }}>
                  🏷️ Detalles de Tarificación (Pricing)
                </Typography>
                {(() => {
                  const pr = statuses?.pricing || message?.pricing;
                  return (
                    <Grid container spacing={1}>
                      <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Facturable:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{pr?.billable ? 'Sí' : 'No'}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Modelo Precios:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{pr?.pricing_model || 'N/A'}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Categoría:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{translateCategory(pr?.category)}</Typography></Grid>

                      <Grid item xs={4}><Typography variant="body2" sx={{ fontWeight: 500 }}>Tipo Tarifa:</Typography></Grid>
                      <Grid item xs={8}><Typography variant="body2">{translatePricingType(pr?.type)}</Typography></Grid>
                    </Grid>
                  );
                })()}
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowDetailsDialog(false)} variant="contained" sx={{ backgroundColor: '#008069', '&:hover': { backgroundColor: '#006653' } }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const handleCatch = () => {
    setError(true);
  };

  return <>{renderMessage()}{renderContextMenuAndDialog()}</>;
}

export default Message;